import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";

/**
 * Top-level branded iframe shell. Per Figma 1:178 — TWO HORIZONTAL regions
 * side-by-side, NOT stacked vertically:
 *   1. PlayerRow      — branded video player + sponsor overlays. The
 *                       inner PlayerFrame locks to 16:9 so the actual
 *                       video surface fills edge-to-edge with no
 *                       letterbox bars regardless of iframe shape.
 *   2. PlaylistColumn — Show: pill row + scrollable rows + scroll
 *                       chevron. Absorbs whatever width is left after
 *                       the player takes its 16:9 slice. On the
 *                       638×226 HLTV preset that leaves ~236px for the
 *                       rail (player ≈ 402px).
 *
 * Earlier V1 had the rail under the player; the Figma redesign moves it to
 * the side so the player can run at full height of the iframe.
 */
export const BrandedShell = styled(Flex)<{
  $accent?: string;
  $accentSoft?: string;
  $accentText?: string;
  $rowBg?: string;
  $rowBorder?: string;
  $pillText?: string;
  $bottomPlaylist?: boolean;
}>`
  /* Mobile layout (player on top, rail below) uses plain @media queries
     against the iframe's own viewport. Inside an iframe, viewport-relative
     media queries resolve against the iframe's bounds, so they work
     identically to container queries here — without the side-effect that
     container-type: inline-size had on the desktop player's height
     chain (it broke the % height inheritance for BrandedPlayer). */
  flex-direction: ${({ $bottomPlaylist }) =>
    $bottomPlaylist ? "column" : "row"};
  height: 100vh;
  width: 100%;

  @media (max-width: 560px) {
    flex-direction: column;
  }
  /* Campaign takeover tokens. When a sponsor is resolved, ALL accent-
     bearing UI adopts the campaign brand color — active row bar, NOW
     PLAYING pill, trending count chip, scrubber fill, play buttons,
     skill tag pills, focus rings, etc. Allstar default blue is the
     fallback when no campaign is live.

       --accent       solid brand color (borders, icons, text on dark)
       --accent-soft  same color at low alpha (tinted backgrounds, glows)
       --accent-text  text color that reads on top of --accent fills
                      (white for most brands, dark for yellow/lime brands)
       --bg           iframe canvas color — what the player + rail sit on
       --row-bg       deep brand color used for playlist row borders +
                      active-row fill. Allstar default is #093358. When
                      a sponsor is active and provides rowBg, it
                      overrides directly. When a sponsor is active but
                      doesn't specify rowBg, the shell derives a deep
                      35% mix of their primary color so rows still
                      adopt the takeover brand instead of staying on
                      the Allstar navy.

     --allstar-accent is kept as an alias of --accent so existing
     consumers (var(--allstar-accent, #3C87D2)) keep working without
     a rename. New code should reference --accent directly. */
  --accent: ${({ $accent }) => $accent ?? "#3C87D2"};
  --accent-soft: ${({ $accentSoft }) =>
    $accentSoft ?? "rgba(60, 135, 210, 0.16)"};
  --accent-text: ${({ $accentText }) => $accentText ?? "#0c1318"};
  --bg: #0c1318;
  /* rowBg resolution:
       1. explicit sponsor.rowBg override          → use as-is
       2. sponsor.primary set but no rowBg         → 35% mix of primary
                                                     into pure black,
                                                     yielding a deep
                                                     brand-tinted chip
       3. no sponsor (Allstar default)             → #093358 */
  --row-bg: ${({ $rowBg, $accent }) =>
    $rowBg ??
    ($accent ? `color-mix(in srgb, ${$accent} 35%, #000)` : "#093358")};
  /* Scoped dark-brand contrast lifts. Only emitted when the frontend
     determined the brand color is too dark to read against --bg; consumers
     fall back to their original derivation (var(--row-border, var(--row-bg)))
     so bright/mid brands and the Allstar default are unaffected.
       --row-border  lighter border for playlist rows + filter pills
       --pill-text   lighter label/count text inside the filter pills */
  ${({ $rowBorder }) => ($rowBorder ? `--row-border: ${$rowBorder};` : "")}
  ${({ $pillText }) => ($pillText ? `--pill-text: ${$pillText};` : "")}
  --allstar-accent: var(--accent);
  --sponsor-accent: var(--accent);
  background: var(--bg);
`;

export const PlayerRow = styled(Box)<{ $bottomPlaylist?: boolean }>`
  /* Left column of the horizontal split. PlayerRow itself is a passive
     wrapper now — the 16:9 aspect-ratio lives on the inner PlayerFrame
     (the actual video surface), so the frame is exactly 16:9 and the
     <video object-fit:contain> fills it with zero letterbox bars. The
     outer row auto-sizes its width to PlayerFrame's outer width
     (frame width + 10px margin).

     Previously aspect-ratio lived here; combined with PlayerFrame's
     5px margin gutters that made the inner content area ~1.815:1
     instead of 1.778:1, pillarboxing the 16:9 video by a few px on
     each side. Moving the constraint to the frame fixes it. */
  flex: 0 0 auto;
  height: 100%;
  position: relative;

  /* Bottom-playlist variant: player runs full iframe width on top.
     Same shape rules as the mobile case below — width-anchored, height
     derived from the inner frame's aspect ratio — but driven by the
     layout flag instead of a viewport-width media query so it triggers
     at desktop iframe sizes too. PlayerFrame inside picks up matching
     overrides via its own $bottomPlaylist prop (bigger margin, so the
     video doesn't feel too big against the iframe edge). */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    width: 100%;
    height: auto;
  `}

  /* Mobile: full-width player on top — PlayerFrame inside flips its
     anchor axis from height-driven to width-driven so the 16:9 video
     fills the iframe width and the rail tucks below. */
  @media (max-width: 560px) {
    width: 100%;
    height: auto;
  }
`;

export const PlaylistColumn = styled(Flex)<{ $bottomPlaylist?: boolean }>`
  /* Right rail of the horizontal split. Now claims whatever width
     remains after the 16:9-locked player takes its share. At the
     standard 638×226 desktop preset that's ~236px (638 - 402); narrower
     iframes shrink the rail further, which is by design — reviewers
     specifically want to see how the rail handles being squeezed. */
  flex: 1 1 auto;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  /* min-width:0 so flex can actually shrink the column below its
     intrinsic content width when the player eats most of the iframe.
     The 280px floor that used to live here was a relic of the old
     fixed-32% split; with the 16:9 player it would force the iframe
     to scroll horizontally on the narrow HLTV embed. */
  min-width: 0;
  /* Anchor for ScrollMoreButton's absolute positioning at the column
     bottom. */
  position: relative;
  /* Horizontal padding lives on the column itself now that rows are
     discrete rounded cards (no more continuous gradient rails to anchor
     to the iframe edge). Asymmetric:
       - left:  4px → 14px from player (player margin 5 + 4 here +
                gap to first row content via row padding)
       - right: 6px → 18px row-right → iframe-right gap on Windows /
                Linux Chrome, where the styled ::-webkit-scrollbar
                reserves 10px of layout width. Math:
                  6 (column padding-right)
                + 10 (scrollbar width)
                + 2 (row margin-right)
                = 18px row-right → iframe-right, so the scrollbar sits
                ~6px inboard of the iframe's right edge instead of
                tucked tight to it. macOS Chrome's overlay scrollbar
                reserves 0px and floats over the rows — we accept that
                since Windows/Linux is the primary HLTV target.
     Top padding is 5px so the front-of-playlist banner (now the column's
     first child on desktop) top-aligns with the video player's top edge —
     the PlayerFrame carries a matching 5px top margin. When no banner is
     present the empty PlaylistBannerHeaderSlot is still a flex item, so the
     10px column gap drops the header pills to ~15px from the top, matching
     the prior 14px breathing room. */
  padding: 5px 6px 0 4px;

  /* Bottom-playlist variant: rail sits BELOW the full-width player at
     desktop sizes too. Same shape as the mobile rail (full-bleed strip,
     pills on top, horizontal scroll of cards below) but with explicit
     bottom padding so the horizontally-scrolling cards have a small
     gap before they kiss the iframe edge — without it the cards bled
     off the bottom in publisher reviews. Gap between the header pills
     and the card row is tightened from the side-rail's 10px to 6px so
     more of the strip's vertical budget goes to the cards themselves.
     The fade gradient on the right edge piggy-backs on the mobile
     selector below — declared here as well so the desktop bottom
     layout gets the same "more to the right" affordance. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    /* In column-flex parent (bottom variant), the base \`height: 100%\`
       resolves to 100vh — which, stacked on top of PlayerRow's 16:9
       height, overflows the iframe and pushes the page-level scroll.
       Force the rail to size from flex instead so PlaylistScrollRegion's
       own overflow-y actually engages. */
    height: auto;
    min-height: 0;
    /* Tighter bottom padding than the prior horizontal strip — the
       bottom fade now overlays the third row to cue scrollability,
       so we don't need extra inset under the cards. */
    padding: 8px 14px 6px;
    gap: 6px;

    /* Bottom-edge fade — vertical analog of the mobile right-edge
       fade. Cues that the third row is cut off and the list keeps
       going below. Front-loaded gradient so the opaque end covers a
       wider band at the bottom; matches the density profile of the
       mobile fade (full canvas color reached at ~70% of the band's
       height). Taller band than mobile (90px vs 60px) because the row
       height is larger here — a 60px fade would only cover ~half a
       row, which read as a soft shadow instead of a hard cut-off. */
    /* The actual fade overlay lives in PlaylistBottomFade (a real
       sibling element rendered from the parent) — pseudo-element
       approach didn't paint reliably over the scroll region across
       browsers, so we render an explicit node instead. */
  `}

  /* Mobile: rail sits BELOW the player as a horizontal strip. The
     interior layout (header pills on top, scrollable region below) stays
     column-flex so the pills don't get caught in the horizontal scroll.
     Width goes full-bleed, height fills the remaining iframe height
     after the 16:9 player. Min-width override needed because the desktop
     280px rule would force a horizontal scrollbar at narrow widths. */
  @media (max-width: 560px) {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    padding: 8px 8px 0;

    /* Right-edge fade — signals "more cards off to the right" beyond
       the viewport. Sits over the scroll rail (z below the chevron, if
       any) and is pointer-events:none so it never intercepts swipes. */
    &::after {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      width: 60px;
      /* Front-loaded gradient — ramps up quickly from transparent so
         the opaque end of the fade covers a wider slice of the right
         edge, hiding the hard card edge against the canvas. */
      background: linear-gradient(
        to right,
        rgba(12, 19, 24, 0) 0%,
        rgba(12, 19, 24, 0.7) 40%,
        var(--bg, #0c1318) 70%
      );
      pointer-events: none;
      z-index: 3;
    }
  }
`;

export const PlaylistHeaderInset = styled(Box)`
  flex: 0 0 auto;
  /* Padding now lives on PlaylistColumn — header inset is a no-op
     spacer kept for layout parity with the prior structure. */
`;

/**
 * DESKTOP placement wrapper for the sponsor playlist banner — hoists it
 * ABOVE the filter pill row (NowPlayingHeader), per Figma 111:1774 where
 * the lead slot sits at the very top of the rail rather than tucked under
 * the "Show:" filters. Column-flex so the banner card's `align-self:
 * stretch` fills the rail width (capped by the card's own max-width),
 * matching exactly how it laid out when it lived inside the scroll region.
 *
 * Collapses on mobile (≤560px), where the banner instead rides inside the
 * horizontal card rail as the first carousel item (PlaylistBannerRailSlot).
 * Only one of the two placement wrappers is ever visible per breakpoint, so
 * the impression beacon still fires exactly once (the hidden `display:none`
 * copy never crosses the IntersectionObserver visibility threshold). */
export const PlaylistBannerHeaderSlot = styled(Box)`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;

  @media (max-width: 560px) {
    display: none;
  }
`;

/**
 * MOBILE placement wrapper for the sponsor playlist banner — keeps it as
 * the first card in the horizontal card rail. `display: contents` so the
 * wrapper box itself doesn't become a flex item: the banner card stays a
 * direct participant of the rail's flex row, so its fixed 225px carousel
 * width applies against the rail (not against an intermediate wrapper).
 *
 * Hidden on desktop (≥561px), where PlaylistBannerHeaderSlot renders the
 * banner above the filters instead. */
export const PlaylistBannerRailSlot = styled(Box)`
  display: none;

  @media (max-width: 560px) {
    display: contents;
  }
`;

export const PlaylistScrollRegion = styled(Box)<{ $bottomPlaylist?: boolean }>`
  display: flex;
  flex-direction: column;
  /* Per Figma — rows are discrete rounded cards with visible vertical
     spacing between them, not abutting blocks. */
  gap: 8px;
  flex: 1 1 auto;
  min-height: 0;

  overflow-y: auto;
  /* Bottom inset leaves room for the absolute-positioned scroll-down
     chevron so the last row never sits underneath it. */
  padding-bottom: 36px;
  /* The row-side half of the 9px gap is handled by each Row's
     margin-right (2px). Scroll region itself extends fully to its
     container's right edge so the scrollbar sits immediately after
     the row's margin gap. */
  padding-right: 0;

  /* Branded scrollbar — track is a faint accent strip, thumb is a
     darkened campaign color so it tracks the takeover theme. Coexists
     with the bottom double-chevron pill: scrollbar carries the visual
     position of the viewport, chevron is the one-tap "more below"
     affordance.

     The scrollbar-color rule keeps the Firefox-styled scrollbar
     matching the campaign theme. */
  scrollbar-color: color-mix(in srgb, var(--accent, #3c87d2) 65%, #000)
    color-mix(in srgb, var(--accent, #3c87d2) 12%, #000);
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    /* 10px is the authoritative scrollbar width on webkit. With
       scrollbar-gutter: stable above, this is also the width that
       gets reserved in layout — locking in the 2 + 10 + 2 = 14 math. */
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: color-mix(in srgb, var(--accent, #3c87d2) 12%, #000);
    border-radius: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: color-mix(in srgb, var(--accent, #3c87d2) 65%, #000);
    border-radius: 6px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: color-mix(in srgb, var(--accent, #3c87d2) 85%, #000);
  }

  /* Bottom-playlist variant: rows stack vertically (full-width cards)
     and scroll the y-axis — same shape as the side-rail desktop layout
     but at the larger card scale. Reviewer feedback was that the prior
     horizontal-strip approach gave the playlist a "carousel" feel that
     undercut the "browse the next clips" intent; vertical stacking
     reads as a proper list. PlaylistColumn's bottom fade overlays the
     last visible row so the third card reads as cut off (scroll cue
     without a chevron). Padding-bottom keeps a small gutter so the
     last row never sits flush against the iframe edge during scroll.

     Scrollbar override: reviewer feedback in this iteration was that
     the default 10px webkit scrollbar didn't read as scrollable in the
     bottom-playlist layout — the bar was visually slim against the
     wider full-width rows. Bumped to 14px so the thumb has visible
     weight and reads as an interactive scrub control. Padding-right
     reserves 4px of breathing room past the scrollbar so it doesn't
     kiss the iframe edge. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    gap: 8px;
    padding-bottom: 12px;
    padding-right: 4px;
    scrollbar-width: auto;
    &::-webkit-scrollbar {
      width: 14px;
    }
  `}

  /* Mobile: rows scroll horizontally as fixed-width cards. Vertical
     scroll is off; horizontal is on. Padding-right flips off (no
     scrollbar gutter to reserve since the horizontal scrollbar lives
     on the bottom edge, not the right). */
  @media (max-width: 560px) {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 8px;
    padding-right: 0;
    /* Webkit scrollbar height only kicks in for horizontal bars — the
       desktop rule above set width but no height, so the horizontal
       bar was collapsing to 0px and reading as un-scrollable on
       Windows. Mirror the 10px desktop bar on the horizontal axis so
       Windows users get the same visible scrub control. */
    &::-webkit-scrollbar {
      height: 10px;
    }
  }
`;

/**
 * "Scroll for more" affordance pinned to the bottom of the rail. Sits
 * alongside the branded scrollbar (not in place of it) — chevron is the
 * one-tap "more below" cue, scrollbar carries the visual position.
 */
/**
 * Bottom-edge fade overlay for the bottom-playlist variant.
 *
 * Sibling of <PlaylistScrollRegion> inside <PlaylistColumn>. PlaylistColumn
 * is position:relative, so this absolutely-positioned overlay anchors to
 * its bottom edge and overlays the lower portion of the scroll region —
 * cuing "more rows below" with a fade-to-canvas gradient that matches
 * the mobile right-edge fade's density profile (transparent → 70% bg at
 * ~40% → solid #0c1318 at ~80%).
 *
 * Rendered as a DOM node (not a ::after pseudo) because the pseudo
 * approach failed to paint reliably over scroll-region rows in some
 * Chrome/Safari combos — an explicit node with z-index sidesteps the
 * stacking-context guesswork.
 */
export const PlaylistBottomFade = styled.div`
  /* Subtle "more below" cue rather than a hard cut-off — earlier
     density (0 → 0.55 → 0.92 → solid over 110px) was reading as a
     dark slab covering the last row. Pull the band down to 60px and
     cap the opaque end at ~0.5 so the underlying row stays legible
     through the fade. */
  background: linear-gradient(
    to bottom,
    rgba(12, 19, 24, 0) 0%,
    rgba(12, 19, 24, 0.18) 50%,
    rgba(12, 19, 24, 0.5) 100%
  );
  bottom: 0;
  height: 60px;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  z-index: 10;
`;

export const ScrollMoreButton = styled.button<{
  $visible: boolean;
  $bottomPlaylist?: boolean;
}>`
  align-items: center;
  appearance: none;
  background: color-mix(
    in srgb,
    var(--accent, #3c87d2) 18%,
    var(--bg, #0c1318)
  );
  border: 1px solid
    color-mix(in srgb, var(--accent, #3c87d2) 55%, var(--bg, #0c1318));
  border-radius: 999px;
  bottom: 6px;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  height: 22px;
  justify-content: center;
  left: 50%;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  padding: 0 10px;
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  position: absolute;
  transform: translateX(-50%);
  transition: opacity 160ms ease;
  width: 48px;
  z-index: 4;

  &:hover {
    background: color-mix(
      in srgb,
      var(--accent, #3c87d2) 32%,
      var(--bg, #0c1318)
    );
  }
  &:focus-visible {
    outline: 2px solid var(--accent, #3c87d2);
    outline-offset: 2px;
  }

  /* Bottom-playlist variant: same reasoning as mobile — the rail runs
     horizontal under the player so the down-chevron points the wrong
     way, the pill row already has its own » chevron, and the strip is
     short enough that the affordance is redundant. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    display: none;
  `}

  /* Mobile: the row-rail chevron is suppressed — the pill row above
     already carries its own » overflow chevron, and stacking two
     "scroll for more" affordances in a short mobile viewport felt
     noisy. Users still get the native horizontal scrollbar + swipe on
     the row rail. */
  @media (max-width: 560px) {
    display: none;
  }
`;
